/**
 * components/wizard/P3PTools.jsx — 🆕 Phase 4 · 3-Punkt-Initialisierung.
 *
 * Präsentations-Bausteine für den P3P-Modus im Anker-Sammelmodus:
 *   · P3PLandmarkChips — gruppierte Landmarken-Palette (Ecken /
 *     Goallinien / Brick / Mittellinie)
 *   · P3POverlay — SVG: Klick-Marker (Fadenkreuz + Nummer) und das
 *     projizierte Feld-Grid (Passkontrolle nach dem 3. Klick)
 * Klick-/Solve-Logik lebt in AnchorCollectMode.jsx.
 */
import React from "react";
import { LINE_CLASS_META } from "./FreeDrawTools";

export const P3P_GROUP_COLORS = {
  Ecken: "#22d3ee",
  Goallinien: "#f59e0b",
  Brick: "#a3e635",
  Mittellinie: "#fb923c",
};

// 🆕 FOV-Kaskaden-Quelle → deutsches UI-Label (geteilt: P3P-Panel,
// Shadow-Monitor, Toasts)
export const fovSourceLabel = (s) =>
  s === "manual_override"
    ? "manuell"
    : s === "shot_override"
      ? "Shot · manuell"
      : s === "shot_curve"
        ? "Shot · Kurve"
        : s === "dlt_selfcal"
          ? "selbstkalibriert"
          : s === "exif"
            ? "EXIF"
            : s?.startsWith?.("profile:")
          ? `Profil ${s.slice(8)}`
          : "Fallback";

export const P3PLandmarkChips = ({ landmarks, active, usedIds, onSelect }) => {
  const groups = [];
  (landmarks || []).forEach((lm) => {
    let g = groups.find((x) => x.name === lm.group);
    if (!g) { g = { name: lm.group, items: [] }; groups.push(g); }
    g.items.push(lm);
  });
  return (
    <div data-testid="p3p-chips" className="space-y-2">
      {groups.map((g) => (
        <div key={g.name}>
          <div
            className="font-mono text-[9px] uppercase tracking-widest mb-1"
            style={{ color: P3P_GROUP_COLORS[g.name] || "#a1a1aa" }}
          >
            {g.name}
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {g.items.map((lm) => {
              const col = P3P_GROUP_COLORS[lm.group] || "#22d3ee";
              const isActive = active === lm.id;
              const used = usedIds.includes(lm.id);
              return (
                <button
                  key={lm.id}
                  data-testid={`p3p-chip-${lm.id}`}
                  onClick={() => onSelect(lm.id)}
                  disabled={used}
                  className={`flex items-center gap-1.5 border rounded-sm px-2 py-1.5 font-mono text-[10px] tracking-wide transition-colors text-left ${
                    isActive ? "ring-2" : used ? "opacity-40" : "opacity-80 hover:opacity-100"
                  }`}
                  style={{
                    borderColor: col,
                    backgroundColor: isActive ? `${col}2e` : `${col}10`,
                    color: col,
                    "--tw-ring-color": col,
                  }}
                  title={lm.label_de}
                >
                  <span className="font-bold shrink-0">{lm.short}</span>
                  <span className="truncate flex-1">{lm.label_de}</span>
                  {used && <span title="Bereits geklickt">✓</span>}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

const gridColor = (cid) => LINE_CLASS_META[cid]?.color || "#34d399";

export const P3POverlay = ({ points, landmarks, gridLines, lines, imgDims, zoom = 1 }) => {
  if (imgDims.w <= 1) return null;
  // 🆕 2026-08-23 · Durch den Zoom geteilt, damit Strichstaerken und
  // Marker auf dem Schirm gleich gross bleiben (siehe FreeDrawTools.jsx).
  const sw = Math.max(2, imgDims.w / 640) / zoom;
  const lmById = Object.fromEntries((landmarks || []).map((l) => [l.id, l]));
  // 🆕 Grid-Linien-Ziehen: Klassen mit aktivem Constraint werden im
  // Grid gedimmt/gestrichelt — der Constraint selbst ist die solide Linie.
  const constrained = new Set((lines || []).map((s) => s.cls));
  return (
    <svg
      data-testid="p3p-overlay"
      className="absolute inset-0 w-full h-full pointer-events-none"
      viewBox={`0 0 ${imgDims.w} ${imgDims.h}`}
      preserveAspectRatio="none"
    >
      {(gridLines || []).map((gl, i) => {
        // 🆕 aux_grid_*: dezentes Hilfsgitter für Zoom-Ausschnitte —
        // dünn + gestrichelt, NICHT greifbar (kein Constraint möglich)
        const isAux = gl.class_id.startsWith("aux_grid");
        return (
          <polyline
            key={`${gl.class_id}-${i}`}
            data-testid={`p3p-grid-${gl.class_id}`}
            points={gl.points.map(([x, y]) => `${x},${y}`).join(" ")}
            fill="none"
            stroke={isAux ? "#94a3b8" : gridColor(gl.class_id)}
            strokeWidth={isAux ? sw * 0.55 : sw}
            strokeOpacity={isAux ? 0.35 : constrained.has(gl.class_id) ? 0.45 : 0.85}
            strokeDasharray={isAux ? "3 7" : constrained.has(gl.class_id) ? "7 6" : undefined}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        );
      })}
      {/* 🆕 Gezogene Linien-Constraints: solide + Endgriffe + Mitte-Griff */}
      {(lines || []).map((s, i) => {
        const col = gridColor(s.cls);
        const r = Math.max(6, imgDims.w / 230) / zoom;
        return (
          <g key={`lc-${i}`} data-testid={`p3p-line-constraint-${s.cls}`}>
            <line x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2}
                  stroke={col} strokeWidth={sw * 1.7} strokeOpacity={0.95}
                  strokeLinecap="round" />
            <circle cx={s.x1} cy={s.y1} r={r} fill={`${col}55`}
                    stroke={col} strokeWidth={sw * 0.8} />
            <circle cx={s.x2} cy={s.y2} r={r} fill={`${col}55`}
                    stroke={col} strokeWidth={sw * 0.8} />
            <circle cx={(s.x1 + s.x2) / 2} cy={(s.y1 + s.y2) / 2} r={r * 0.75}
                    fill="none" stroke={col} strokeWidth={sw * 0.55}
                    strokeDasharray="3 3" />
          </g>
        );
      })}
      {(points || []).map((p, i) => {
        const lm = lmById[p.landmark_id];
        const col = lm ? (P3P_GROUP_COLORS[lm.group] || "#22d3ee") : "#22d3ee";
        const r = Math.max(7, imgDims.w / 180) / zoom;
        return (
          <g key={i} data-testid={`p3p-point-${i}`}>
            {/* Griff-Ring: Punkte sind nachziehbar (Feinjustierung) */}
            <circle cx={p.x} cy={p.y} r={r * 1.7} fill="none" stroke={col}
                    strokeWidth={sw * 0.5} strokeOpacity={0.5} strokeDasharray="4 4" />
            <circle cx={p.x} cy={p.y} r={r} fill={`${col}55`} stroke={col} strokeWidth={sw} />
            <line x1={p.x - r * 0.55} y1={p.y} x2={p.x + r * 0.55} y2={p.y} stroke={col} strokeWidth={sw * 0.7} />
            <line x1={p.x} y1={p.y - r * 0.55} x2={p.x} y2={p.y + r * 0.55} stroke={col} strokeWidth={sw * 0.7} />
            <text
              x={p.x + r * 1.3} y={p.y - r * 0.6}
              fill={col} fontSize={Math.max(13, imgDims.w / 85) / zoom}
              fontFamily="monospace" stroke="#000" strokeWidth={0.6} paintOrder="stroke"
            >
              {i + 1}·{lm?.short || p.landmark_id}
            </text>
          </g>
        );
      })}
    </svg>
  );
};
